import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Tooltip,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import DnsIcon from "@mui/icons-material/Dns";
import BugReportIcon from "@mui/icons-material/BugReport";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import Chart from "react-apexcharts";
import { colors } from "../../../theme";
import CircularIndeterminate from "../../../components/circularProgress";

const tableStyle = {
  ".MuiTableCell-root": {
    fontSize: 12,
    borderBottom: "1px solid rgba(68,144,149,0.18)",
    color: "#eaeaea",
    py: 0.75,
    px: 1,
  },
  ".MuiTableCell-head": {
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontSize: 10,
    backgroundColor: "transparent",
    color: "#449095",
  },
  ".MuiTableRow-root": {
    transition: "background-color .15s ease",
    "&:hover .MuiTableCell-body": {
      backgroundColor: "rgba(68,144,149,0.07)",
    },
  },
};

const cardSx = {
  backgroundColor: "rgba(255,255,255,0.03)",
  borderRadius: "10px",
  border: "1px solid rgba(68,144,149,0.22)",
  p: 1.5,
};

const SectionHeader = ({ icon: Icon, title, total, iconColor }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    pb={0.75}
    mb={0.5}
    borderBottom="1px solid rgba(68,144,149,0.3)"
  >
    <Box display="flex" alignItems="center" gap={0.75}>
      <Icon sx={{ color: iconColor || "#449095", fontSize: 16 }} />
      <Typography fontSize="0.85rem" fontWeight={600} color={colors.grey[100]}>
        {title}
      </Typography>
    </Box>
    <Chip
      label={total ?? "—"}
      size="small"
      sx={{
        height: 18,
        fontSize: "0.68rem",
        backgroundColor: "rgba(68,144,149,0.15)",
        color: "#449095",
        fontWeight: 700,
        border: "1px solid rgba(68,144,149,0.45)",
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  </Box>
);

const IpTable = ({ icon, title, total, iconColor, columns, rows }) => (
  <Box sx={cardSx}>
    <SectionHeader icon={icon} title={title} total={total} iconColor={iconColor} />
    <TableContainer sx={{ maxHeight: 180, overflowY: "auto" }} className="highlights">
      <Table stickyHeader size="small" sx={tableStyle}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} sx={{ width: col.width }}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows && rows.length > 0 ? (
            rows.map((row, i) => (
              <TableRow key={i}>
                {columns.map((col) => (
                  <TableCell key={col.key}>
                    {col.ellipsis ? (
                      <Tooltip title={String(row[col.key] ?? "")} placement="top-start">
                        <Typography
                          fontSize={11}
                          color="#eaeaea"
                          noWrap
                          sx={{
                            maxWidth: col.maxWidth || "18ch",
                            display: "block",
                            cursor: "default",
                            fontFamily: col.mono ? "monospace" : "inherit",
                            "&:hover": { color: col.hoverColor || "#eaeaea" },
                          }}
                        >
                          {row[col.key]}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        fontSize={11}
                        color="#eaeaea"
                        sx={{ fontFamily: col.mono ? "monospace" : "inherit" }}
                      >
                        {row[col.key]}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography fontSize={11} color="rgba(255,255,255,0.4)" textAlign="center">
                  No records
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const donutOptions = {
  chart: { animations: { enabled: false }, type: "donut", background: "transparent" },
  plotOptions: { pie: { donut: { size: "58%" } } },
  colors: ["#791F2A", "#E7D763", "#68BBBC"],
  dataLabels: { enabled: false },
  labels: ["Malware", "URL", "Passive DNS"],
  legend: {
    fontSize: "10px",
    labels: { colors: "#eaeaea" },
    itemMargin: { horizontal: 6, vertical: 0 },
  },
  stroke: { show: false },
  tooltip: { fillSeriesColor: true },
  theme: { mode: "dark" },
};

export const IpPanel = ({ data, charged }) => {
  if (!charged) {
    return (
      <Box p={3}>
        <CircularIndeterminate charged={false} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="rgba(255,255,255,0.5)">
          No data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={1.5} display="flex" flexDirection="column" gap={1.5}>
      {/* IP + Location */}
      <Box sx={cardSx}>
        <Box display="flex" alignItems="center" gap={1} mb={0.75}>
          <NetworkCheckIcon sx={{ color: "#449095", fontSize: 20 }} />
          <Typography
            variant="h5"
            sx={{ fontWeight: 900, color: "#eaeaea", wordBreak: "break-all", letterSpacing: 0.5 }}
          >
            {data.ip}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.75}>
          <LocationOnIcon sx={{ color: "#449095", fontSize: 16 }} />
          <Typography variant="body2" sx={{ fontWeight: 500, color: "rgba(255,255,255,0.75)" }}>
            {data.location}
          </Typography>
        </Box>
      </Box>

      {/* Donut chart */}
      <Box sx={{ ...cardSx, p: 1 }}>
        <Chart
          options={donutOptions}
          series={[data.count_malware || 0, data.count_url_list || 0, data.count_passive_dns || 0]}
          type="donut"
          height={190}
          width="100%"
        />
      </Box>

      {/* URL Table */}
      <IpTable
        icon={LanguageIcon}
        title="Associated URLs"
        total={data.count_url_list}
        iconColor={colors.greenAccent[400]}
        columns={[
          { key: "date", label: "Date", width: "35%" },
          { key: "url", label: "URL", ellipsis: true, maxWidth: "20ch", hoverColor: colors.greenAccent[400] },
        ]}
        rows={data.last_5_url_list}
      />

      {/* Passive DNS Table */}
      <IpTable
        icon={DnsIcon}
        title="Passive DNS"
        total={data.count_passive_dns}
        iconColor="#449095"
        columns={[
          { key: "last", label: "Last Date", width: "38%" },
          { key: "hostname", label: "Hostname", ellipsis: true, maxWidth: "18ch" },
        ]}
        rows={data.last_5_passive_dns}
      />

      {/* Malware Table */}
      <IpTable
        icon={BugReportIcon}
        title="Malware"
        total={data.count_malware}
        iconColor={colors.redAccent[500]}
        columns={[
          { key: "date", label: "Date", width: "35%" },
          { key: "idMalware", label: "Hash / Detection", mono: true, ellipsis: true, maxWidth: "18ch" },
        ]}
        rows={data.last_5_malware}
      />
    </Box>
  );
};
